import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useDiagnosticsContext } from '../hooks/useDiagnosticsContext';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  IconButton,
  Alert,
  Card,
  CardContent,
  Divider,
  Tab,
  Tabs,
  Link,
  Skeleton,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  TextField
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Work as WorkIcon,
  Business as BusinessIcon,
  ContentCopy as DuplicateIcon,
  WarningAmber as WarningAmberIcon
} from '@mui/icons-material';
import { customerService, UpdateCustomerDto, CustomerSource } from '../services/customerService';
import { Job, CreateJobDto, UpdateJobDto, JobType, Priority, DuplicateJobDto } from '../services/jobService';
import { Appointment } from '../services/appointmentService';
import { AppointmentType } from '../services/appointmentTypeService';
import { Business } from '../services/businessService';
import { useAuth } from '../contexts/AuthContext';
import { useBusinessTheme } from '../contexts/BusinessThemeContext';
import BaseDialog from '../components/common/BaseDialog';
import GoogleMapsAutocomplete from '../components/common/GoogleMapsAutocomplete';
import JobForm from '../components/common/JobForm';
import JobDetailPage from './JobDetailPage';
import DuplicateJobDialog from '../components/jobDetail/DuplicateJobDialog';
import CustomerDetailsCard from '../components/common/CustomerDetailsCard';
import TabPanel from '../components/common/TabPanel';
import {
  useCustomer,
  useJobsByCustomer,
  useAppointmentsByCustomer,
  useAppointmentTypes,
  useBusinesses,
  useBusinessUsers,
  useUpdateCustomer,
  useDeleteCustomer,
  useCreateJob,
  useUpdateJob,
  useDuplicateJob,
} from '../hooks/queries';
import { sortUsers } from '../utils/userHelpers';
import { extractApiErrorMessage } from '../utils/apiUtils';

// Wrapper component to embed JobDetailPage with jobId and appointments props
const EmbeddedJobDetailPage: React.FC<{
  jobId: string;
  customerId: string;
  appointments: Appointment[];
  currentCustomerId: string;
  appointmentTypes: AppointmentType[];
  business: Business | null;
}> = ({ jobId, customerId, appointments, currentCustomerId, appointmentTypes, business }) => {
  // Parse URL to determine which sub-tab should be selected in JobDetailPage
  const pathname = window.location.pathname;
  const pathParts = pathname.split('/').filter(Boolean);

  // Expected format: crm/customers/{id}/jobs/{jobId}/[subtab]
  let initialTab = 0; // Default to Overview
  if (pathParts.length >= 6 && pathParts[0] === 'crm' && pathParts[1] === 'customers' &&
      pathParts[2] === currentCustomerId && pathParts[3] === 'jobs' && pathParts[4] === jobId) {
    const subTab = pathParts[5];
    if (subTab === 'communications') {
      initialTab = 1; // Communications tab
    } else if (subTab === 'documents') {
      initialTab = 2; // Documents tab
    }
    // Form tabs will be handled by JobDetailPage's useEffect after forms load
  }

  return (
    <div style={{ pointerEvents: 'auto' }}>
      <JobDetailPage
        jobId={jobId}
        embedded={true}
        customerAppointments={appointments}
        initialTab={initialTab}
        customerId={customerId}
        parentAppointmentTypes={appointmentTypes}
        parentBusiness={business}
      />
    </div>
  );
};

const CustomerDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { theme } = useBusinessTheme();

  // TanStack Query hooks for data fetching
  const {
    data: customer,
    isLoading: customerLoading,
    isFetching: customerFetching,
    isStale: customerStale,
    error: customerError
  } = useCustomer(id);

  useDiagnosticsContext(
    'customer',
    customer ? `Customer: ${customer.firstName} ${customer.lastName} (${customer.id})` : null,
  );
  const {
    data: jobsData,
    isLoading: jobsLoading,
    isFetching: jobsFetching,
    isStale: jobsStale
  } = useJobsByCustomer(id);
  const {
    data: appointmentsData,
    isLoading: appointmentsLoading,
    isFetching: appointmentsFetching,
    isStale: appointmentsStale
  } = useAppointmentsByCustomer(id);
  const { data: appointmentTypesData } = useAppointmentTypes({ businessId: user?.businessId ?? undefined });
  const { data: businessesData } = useBusinesses();
  const { data: businessUsersData } = useBusinessUsers({ businessId: user?.businessId ?? undefined });

  // Mutations
  const updateCustomerMutation = useUpdateCustomer();
  const deleteCustomerMutation = useDeleteCustomer();
  const createJobMutation = useCreateJob();
  const updateJobMutation = useUpdateJob();
  const duplicateJobMutation = useDuplicateJob();

  // Derive data from queries
  const jobs = useMemo(() => {
    if (!jobsData) return [];
    return [...jobsData].sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime());
  }, [jobsData]);
  const appointments = appointmentsData || [];
  const appointmentTypes = appointmentTypesData || [];
  const business = businessesData?.[0] ?? null;
  const businessUsers = useMemo(() => sortUsers(businessUsersData || []), [businessUsersData]);

  // Loading state (customer is main loading indicator)
  const loading = customerLoading;

  // UI state
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isJobDialogOpen, setIsJobDialogOpen] = useState(false);
  const [isJobEditDialogOpen, setIsJobEditDialogOpen] = useState(false);
  const [isDuplicateDialogOpen, setIsDuplicateDialogOpen] = useState(false);
  const [duplicateAddress, setDuplicateAddress] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [newlyCreatedJobId, setNewlyCreatedJobId] = useState<string | null>(null);
  const hasSetInitialTab = useRef(false);

  // Helper function to get the currently selected job
  const getSelectedJob = () => {
    if (tabValue >= 3 && jobs.length > 0) {
      const jobIndex = tabValue - 3; // Job tabs start at index 3 (after Overview, Appointments, Add Job)
      return jobs[jobIndex] || null;
    }
    return null;
  };
  const [jobFormData, setJobFormData] = useState<CreateJobDto>({
    customerId: '',
    assignedSalesPersonId: user?.id || '',
    jobAddress: '',
    type: JobType.Consultation,
    estimatedValue: 0,
    priority: Priority.Medium,
    notes: ''
  });
  const [jobEditFormData, setJobEditFormData] = useState<UpdateJobDto>({
    id: '',
    customerId: '',
    assignedSalesPersonId: user?.id || '',
    type: JobType.Consultation,
    estimatedValue: 0,
    priority: Priority.Medium
  });
  const [formData, setFormData] = useState<UpdateCustomerDto>({
    id: '',
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    address: '',
    city: '',
    country: '',
    source: CustomerSource.Other
  } as UpdateCustomerDto);

  // Debug logging for stale-while-revalidate behavior
  useEffect(() => {
    console.log('[CustomerDetailPage] Query state:', {
      customer: {
        isLoading: customerLoading,
        isFetching: customerFetching,
        isStale: customerStale,
        hasData: !!customer,
      },
      jobs: {
        isLoading: jobsLoading,
        isFetching: jobsFetching,
        isStale: jobsStale,
        hasData: !!jobsData,
        count: jobsData?.length ?? 0,
      },
      appointments: {
        isLoading: appointmentsLoading,
        isFetching: appointmentsFetching,
        isStale: appointmentsStale,
        hasData: !!appointmentsData,
        count: appointmentsData?.length ?? 0,
      },
      customerId: id,
    });
  }, [customerLoading, customerFetching, customerStale, customer, jobsLoading, jobsFetching, jobsStale, jobsData, appointmentsLoading, appointmentsFetching, appointmentsStale, appointmentsData, id]);

  // Set error from query error
  useEffect(() => {
    if (customerError) {
      setError((customerError as Error).message || 'Failed to load customer data');
    }
  }, [customerError]);

  // Set initial tab based on URL path (only once after initial data load)
  useEffect(() => {
    if (hasSetInitialTab.current || jobs.length === 0 || loading) return;

    const pathname = location.pathname;
    const pathParts = pathname.split('/').filter(Boolean);

    if (pathParts.length >= 3 && pathParts[0] === 'crm' && pathParts[1] === 'customers' && pathParts[2] === id) {
      if (pathParts[3] === 'appointments') {
        setTabValue(1);
      } else if (pathParts[3] === 'jobs' && pathParts[4]) {
        const jobId = pathParts[4];
        const jobIndex = jobs.findIndex(job => job.id === jobId);
        if (jobIndex !== -1) {
          setTabValue(3 + jobIndex);
        }
      }
      hasSetInitialTab.current = true;
    }
  }, [jobs.length, loading, id]);

  // Handle navigation with selectedJobId, newJobData from location state, or URL path
  useEffect(() => {
    if (location.state?.newJobData && jobs.length > 0) {
      // Find the newly created job in the query data and navigate to it
      const newJobIndex = jobs.findIndex(job => job.id === location.state.newJobData.id);
      if (newJobIndex !== -1) {
        setTabValue(3 + newJobIndex);
        updateUrlForTab(2 + newJobIndex);
      }
    } else if (jobs.length > 0 && location.state?.selectedJobId) {
      const selectedJobIndex = jobs.findIndex(job => job.id === location.state.selectedJobId);
      if (selectedJobIndex !== -1) {
        // Job tabs start at index 3 (Overview=0, Appointments=1, Add Job=2, Jobs=3+)
        setTabValue(3 + selectedJobIndex);
        updateUrlForTab(2 + selectedJobIndex);
      }
    } else if (jobs.length > 0) {
      // Check if URL contains a specific job ID
      const pathname = window.location.pathname;
      const pathParts = pathname.split('/').filter(Boolean);

      if (pathParts.length >= 5 && pathParts[0] === 'crm' && pathParts[1] === 'customers' &&
          pathParts[2] === id && pathParts[3] === 'jobs' && pathParts[4]) {
        const jobId = pathParts[4];
        const selectedJobIndex = jobs.findIndex(job => job.id === jobId);
        if (selectedJobIndex !== -1) {
          setTabValue(3 + selectedJobIndex);
        }
      }
    }
  }, [jobs, location.state, id]);

  // Auto-dismiss success messages
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Switch to newly created job tab when jobs are loaded
  useEffect(() => {
    if (newlyCreatedJobId && jobs.length > 0) {
      const newJobIndex = jobs.findIndex(job => job.id === newlyCreatedJobId);
      if (newJobIndex !== -1) {
        // Job tabs start at index 3 (Overview=0, Appointments=1, Add Job=2, Jobs=3+)
        setTabValue(3 + newJobIndex);
        setNewlyCreatedJobId(null); // Reset the flag
      }
    }
  }, [jobs, newlyCreatedJobId]);

  const handleEditClick = () => {
    if (!customer) return;

    setFormData({
      id: customer.id,
      firstName: customer.firstName || '',
      lastName: customer.lastName || '',
      email: customer.email || '',
      phoneNumber: customer.phoneNumber || '',
      address: customer.address || '',
      city: customer.city || '',
      country: customer.country || '',
      secondaryContactName: customer.secondaryContactName || '',
      secondaryContactPhone: customer.secondaryContactPhone || '',
      secondaryContactEmail: customer.secondaryContactEmail || '',
      source: customer.source || CustomerSource.Other,
      assignedSalesPersonId: customer.assignedSalesPersonId,
      isDifficultPayer: customer.isDifficultPayer,
      difficultPayerNotes: customer.difficultPayerNotes || ''
    } as UpdateCustomerDto);

    setIsEditDialogOpen(true);
  };

  const handleCreateJobClick = () => {
    if (!customer) return;

    setJobFormData({
      customerId: customer.id,
      assignedSalesPersonId: customer.assignedSalesPersonId || user?.id || '',
      jobAddress: customer.address || '',
      type: JobType.Consultation,
      estimatedValue: 0,
      priority: Priority.Medium,
      notes: ''
    });

    setIsJobDialogOpen(true);
  };

  const handleCreateJob = async () => {
    createJobMutation.mutate(jobFormData, {
      onSuccess: (createdJob) => {
        setIsJobDialogOpen(false);
        setSuccess('Job created successfully');
        setError(null);
        // Set the newly created job ID - the useEffect will handle switching to the tab
        setNewlyCreatedJobId(createdJob.id);
      },
      onError: (err: any) => {
        setError(err.message || 'Failed to create job');
        setSuccess(null);
      },
    });
  };

  const handleUpdateCustomer = async () => {
    if (!customer) return;

    // Clean up empty strings to undefined for optional fields
    const cleanedFormData = {
      ...formData,
      email: formData.email || undefined,
      phoneNumber: formData.phoneNumber || undefined,
      address: formData.address || undefined,
      city: formData.city || undefined,
      country: formData.country || undefined,
      secondaryContactName: formData.secondaryContactName || undefined,
      secondaryContactPhone: formData.secondaryContactPhone || undefined,
      secondaryContactEmail: formData.secondaryContactEmail || undefined,
      isDifficultPayer: formData.isDifficultPayer,
      difficultPayerNotes: formData.isDifficultPayer ? formData.difficultPayerNotes || undefined : '',
    };

    updateCustomerMutation.mutate(cleanedFormData, {
      onSuccess: () => {
        setIsEditDialogOpen(false);
        setSuccess('Customer updated successfully');
        setError(null);
      },
      onError: (err: any) => {
        setError(extractApiErrorMessage(err, 'Failed to update customer'));
        setSuccess(null);
      },
    });
  };

  const handleDeleteCustomer = async () => {
    if (!customer || !window.confirm('Are you sure you want to delete this customer? This will also delete all associated jobs and appointments.')) return;

    deleteCustomerMutation.mutate(customer.id, {
      onSuccess: () => {
        navigate('/crm/customers');
      },
      onError: (err: any) => {
        setError(err.message || 'Failed to delete customer');
      },
    });
  };

  const handleBack = () => {
    navigate('/crm/customers');
  };

  const handleEditJob = () => {
    const selectedJob = getSelectedJob();
    if (!selectedJob) return;

    // Convert string enum values to numeric enum values for form
    const convertJobType = (type: JobType | string): JobType => {
      if (typeof type === 'string') {
        const stringToEnum: { [key: string]: JobType } = {
          'KitchenDesign': JobType.KitchenDesign,
          'KitchenRemodel': JobType.KitchenRemodel,
          'NewKitchen': JobType.NewKitchen,
          'Consultation': JobType.Consultation,
          'Renovation': JobType.Renovation,
          'Other': JobType.Other
        };
        return stringToEnum[type] || JobType.Consultation;
      }
      return type as JobType;
    };

    const convertPriority = (priority: Priority | string): Priority => {
      if (typeof priority === 'string') {
        const stringToEnum: { [key: string]: Priority } = {
          'Low': Priority.Low,
          'Medium': Priority.Medium,
          'High': Priority.High,
          'Urgent': Priority.Urgent
        };
        return stringToEnum[priority] || Priority.Medium;
      }
      return priority as Priority;
    };

    setJobEditFormData({
      id: selectedJob.id,
      customerId: selectedJob.customerId,
      assignedSalesPersonId: selectedJob.assignedSalesPersonId,
      jobAddress: selectedJob.jobAddress || '',
      type: convertJobType(selectedJob.type),
      estimatedValue: selectedJob.estimatedValue,
      actualValue: selectedJob.actualValue,
      priority: convertPriority(selectedJob.priority),
      expectedCompletionDate: selectedJob.expectedCompletionDate,
      notes: selectedJob.notes || ''
    });

    setIsJobEditDialogOpen(true);
  };

  const handleUpdateJob = async () => {
    const selectedJob = getSelectedJob();
    if (!selectedJob) return;

    // Clean up form data - keep notes as empty string if not provided
    const cleanedFormData = {
      ...jobEditFormData,
      jobAddress: jobEditFormData.jobAddress || undefined,
      expectedCompletionDate: jobEditFormData.expectedCompletionDate || undefined,
      notes: jobEditFormData.notes || ''
    };

    updateJobMutation.mutate(cleanedFormData, {
      onSuccess: () => {
        setIsJobEditDialogOpen(false);
        setSuccess('Job updated successfully');
        setError(null);
      },
      onError: (err: any) => {
        setError(extractApiErrorMessage(err, 'Failed to update job'));
        setSuccess(null);
      },
    });
  };

  const handleDuplicateJob = () => {
    const selectedJob = getSelectedJob();
    if (!selectedJob) return;

    setDuplicateAddress(selectedJob.jobAddress || '');
    setIsDuplicateDialogOpen(true);
  };

  const handleConfirmDuplicateJob = async () => {
    const selectedJob = getSelectedJob();
    if (!selectedJob) return;

    setError(null);

    const duplicateDto: DuplicateJobDto = {
      sourceJobId: selectedJob.id,
      jobAddress: duplicateAddress || undefined
    };

    duplicateJobMutation.mutate(duplicateDto, {
      onSuccess: (newJob) => {
        setIsDuplicateDialogOpen(false);
        setSuccess('Job duplicated successfully.');
        // Navigate back to customer detail page with the new job data
        navigate(`/crm/customers/${id}`, {
          state: { newJobData: newJob }
        });
      },
      onError: (err: any) => {
        setError(extractApiErrorMessage(err, 'Failed to duplicate job'));
      },
    });
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    // Index 2 is the "Add Job" tab - open dialog instead of changing tab
    if (newValue === 2) {
      handleCreateJobClick();
      return;
    }
    setTabValue(newValue);
    updateUrlForTab(newValue);
  };

  const updateUrlForTab = (tabIndex: number) => {
    if (!id) return;

    let newPath = `/crm/customers/${id}`;

    if (tabIndex === 1) {
      newPath = `/crm/customers/${id}/appointments`;
    } else if (tabIndex >= 3) {
      const jobIndex = tabIndex - 3;
      if (jobs && jobs.length > jobIndex && jobs[jobIndex]) {
        newPath = `/crm/customers/${id}/jobs/${jobs[jobIndex].id}`;
      } else {
        return;
      }
    }

    navigate(newPath, { replace: true });
  };


  const handleAppointmentClick = (appointment: Appointment) => {
    const appointmentDate = new Date(appointment.startTime);
    navigate('/calendar', { 
      state: { 
        date: appointmentDate.toISOString(),
        appointmentId: appointment.id 
      } 
    });
  };

  const getCustomerSourceDisplayName = (source: CustomerSource | string | undefined): string => {
    if (!source) return 'Unknown';

    const sourceMap: { [key: string]: string } = {
      [CustomerSource.Website]: 'Website',
      [CustomerSource.Referral]: 'Referral',
      [CustomerSource.WalkIn]: 'Walk-In',
      [CustomerSource.Advertising]: 'Advertising',
      [CustomerSource.SocialMedia]: 'Social Media',
      [CustomerSource.EmailMarketing]: 'Email Marketing',
      [CustomerSource.Other]: 'Other',
      [CustomerSource.PreviousClient]: 'Previous Client',
      [CustomerSource.PhoneIn]: 'Phone In'
    };

    return sourceMap[source.toString()] || source.toString();
  };

  const getAppointmentTypeColor = (appointmentTypeId: string): string => {
    const appointmentType = appointmentTypes.find(type => type.id === appointmentTypeId);
    return appointmentType ? appointmentType.color : '#ccc';
  };

  if (error && !customer && !loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={handleBack} startIcon={<ArrowBackIcon />}>
          Back to Customers
        </Button>
      </Box>
    );
  }

  if (!loading && !customer) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Customer not found
        </Alert>
        <Button variant="contained" onClick={handleBack} startIcon={<ArrowBackIcon />}>
          Back to Customers
        </Button>
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>{customer ? `${customer.firstName} ${customer.lastName}` : 'Customer'} | MindShare Calendar</title>
      </Helmet>

      <Box sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <IconButton 
              onClick={handleBack} 
              color="primary"
              sx={{ display: { xs: 'inline-flex', md: 'none' } }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box>
              {loading || !customer ? (
                <>
                  <Skeleton variant="text" width={200} height={40} />
                  <Skeleton variant="text" width={100} height={24} />
                </>
              ) : (
                <>
                  <Typography variant="h4" component="h1">
                    {customer.firstName} {customer.lastName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Customer
                  </Typography>
                </>
              )}
            </Box>
          </Box>
          <Box display="flex" gap={1}>
            {loading ? (
              <>
                <Skeleton variant="rectangular" width={120} height={36} />
                <Skeleton variant="rectangular" width={140} height={36} />
              </>
            ) : (
              <>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleCreateJobClick}
                >
                  Create Job
                </Button>
                {getSelectedJob() && (
                  <>
                    <Button
                      variant="outlined"
                      startIcon={<DuplicateIcon />}
                      onClick={handleDuplicateJob}
                    >
                      Duplicate Job
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={handleEditJob}
                    >
                      Edit Job
                    </Button>
                  </>
                )}
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleDeleteCustomer}
                  sx={{ display: 'none' }}
                >
                  Delete
                </Button>
              </>
            )}
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Tabs */}
        <Paper sx={{ backgroundColor: theme.accentColor, mb: 3, borderRadius: 0 }}>
          <Box sx={{ overflowX: 'auto' }}>
            {loading ? (
              <Box sx={{ display: 'flex', gap: 2, p: 1 }}>
                <Skeleton variant="rectangular" width={100} height={48} />
                <Skeleton variant="rectangular" width={140} height={48} />
                <Skeleton variant="rectangular" width={160} height={48} />
              </Box>
            ) : (
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                aria-label="customer detail tabs"
                variant="scrollable"
                scrollButtons="auto"
              >
                <Tab label="Overview" />
                <Tab label="Appointments" />
                <Tab
                  icon={<AddIcon />}
                  iconPosition="start"
                  label="Add Job"
                  sx={{
                    minHeight: 48,
                    color: 'primary.main',
                  }}
                />
                {jobs.map((job, index) => (
                  <Tab
                    key={job.id}
                    icon={<WorkIcon />}
                    iconPosition="start"
                    label={`${job.jobAddress ? job.jobAddress.split(',')[0] : 'No Address'}`}
                  />
                ))}
              </Tabs>
            )}
          </Box>
        </Paper>

        {/* Tab Panels */}
        <TabPanel value={tabValue} index={0}>
          {/* Difficult Payer Warning Banner */}
          {customer?.isDifficultPayer && (
            <Alert
              severity="warning"
              icon={<WarningAmberIcon />}
              sx={{ mb: 3 }}
            >
              <Typography variant="subtitle2" fontWeight="bold">
                Difficult Payer
              </Typography>
              {customer.difficultPayerNotes && (
                <Typography variant="body2">
                  {customer.difficultPayerNotes}
                </Typography>
              )}
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* Customer Details Card */}
            <Grid item xs={12} md={6}>
              <Card sx={{ backgroundColor: theme.accentColor }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Contact Information
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  {loading ? (
                    <>
                      {Array.from({ length: 6 }).map((_, index) => (
                        <Box key={index} display="flex" alignItems="center" sx={{ mb: 2 }}>
                          <Skeleton variant="circular" width={20} height={20} sx={{ mr: 1.5 }} />
                          <Box flex={1}>
                            <Skeleton variant="text" width={100} height={16} />
                            <Skeleton variant="text" width={200} height={24} />
                          </Box>
                        </Box>
                      ))}
                    </>
                  ) : customer ? (
                    <>
                      <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                    <PersonIcon sx={{ fontSize: 20, mr: 1.5, color: 'text.secondary' }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Name
                      </Typography>
                      <Typography variant="body1">
                        {customer.firstName} {customer.lastName}
                      </Typography>
                    </Box>
                  </Box>

                  {customer.email && (
                    <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                      <EmailIcon sx={{ fontSize: 20, mr: 1.5, color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Email
                        </Typography>
                        <Typography variant="body1">
                          {customer.email}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {customer.phoneNumber && (
                    <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                      <PhoneIcon sx={{ fontSize: 20, mr: 1.5, color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Phone
                        </Typography>
                        <Typography variant="body1">
                        <Link
                          href={`tel:${customer.phoneNumber}`}
                          variant="body1"
                          sx={{
                            textDecoration: 'none',
                            color: 'text.primary',
                            cursor: 'pointer',
                            '&:hover': {
                              textDecoration: 'underline',
                              color: 'primary.main',
                              cursor: 'pointer'
                            }
                          }}
                        >
                          {customer.phoneNumber}
                        </Link>
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {(customer.address || customer.city || customer.state || customer.zipCode) && (
                    <Box display="flex" alignItems="flex-start" sx={{ mb: 2 }}>
                      <LocationIcon sx={{ fontSize: 20, mr: 1.5, color: 'text.secondary', mt: 0.5 }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Address
                        </Typography>
                        <Typography variant="body1">
                          {[customer.address, customer.city, customer.state, customer.zipCode]
                            .filter(Boolean)
                            .join(', ')}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {(customer.secondaryContactName || customer.secondaryContactPhone || customer.secondaryContactEmail) && (
                    <>
                      {customer.secondaryContactName && (
                        <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                          <PersonIcon sx={{ fontSize: 20, mr: 1.5, color: 'text.secondary' }} />
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Secondary Contact
                            </Typography>
                            <Typography variant="body1">
                              {customer.secondaryContactName}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                      {customer.secondaryContactPhone && (
                        <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                          <PhoneIcon sx={{ fontSize: 20, mr: 1.5, color: 'text.secondary' }} />
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Secondary Phone
                            </Typography>
                            <Typography variant="body1">
                              <Link
                                href={`tel:${customer.secondaryContactPhone}`}
                                variant="body1"
                                sx={{
                                  textDecoration: 'none',
                                  color: 'text.primary',
                                  cursor: 'pointer',
                                  '&:hover': {
                                    textDecoration: 'underline',
                                    color: 'primary.main',
                                    cursor: 'pointer'
                                  }
                                }}
                              >
                                {customer.secondaryContactPhone}
                              </Link>
                            </Typography>
                          </Box>
                        </Box>
                      )}
                      {customer.secondaryContactEmail && (
                        <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                          <EmailIcon sx={{ fontSize: 20, mr: 1.5, color: 'text.secondary' }} />
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Secondary Email
                            </Typography>
                            <Typography variant="body1">
                              {customer.secondaryContactEmail}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </>
                  )}

                  <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                    <BusinessIcon sx={{ fontSize: 20, mr: 1.5, color: 'text.secondary' }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Source
                      </Typography>
                      <Typography variant="body1">
                        {getCustomerSourceDisplayName(customer.source)}
                      </Typography>
                    </Box>
                  </Box>

                  {customer.assignedSalesPersonName && (
                    <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                      <PersonIcon sx={{ fontSize: 20, mr: 1.5, color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Assigned Sales Person
                        </Typography>
                        <Typography variant="body1">
                          {customer.assignedSalesPersonName}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                    <CalendarIcon sx={{ fontSize: 20, mr: 1.5, color: 'text.secondary' }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Created Date
                      </Typography>
                      <Typography variant="body1">
                        {new Date(customer.createdDate).toLocaleDateString('en-GB')}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant="contained"
                      startIcon={<EditIcon />}
                      onClick={handleEditClick}
                      size="small"
                    >
                      Edit
                    </Button>
                  </Box>
                    </>
                  ) : null}
                </CardContent>
              </Card>
            </Grid>

            {/* No Jobs Alert */}
            {!loading && jobs.length === 0 && (
              <Grid item xs={12} md={6}>
                <Alert
                  severity="info"
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={handleCreateJobClick}
                    >
                      Add Job
                    </Button>
                  }
                >
                  There are currently no jobs for this customer.
                </Alert>
              </Grid>
            )}
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Card sx={{ backgroundColor: theme.accentColor }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Appointments</Typography>

              {appointmentsLoading ? (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {Array.from({ length: 3 }).map((_, index) => (
                    <Grid item xs={12} key={index}>
                      <Card variant="outlined" sx={{ borderLeft: '10px solid #ccc' }}>
                        <CardContent>
                          <Skeleton variant="text" width="60%" height={32} />
                          <Skeleton variant="text" width="80%" height={20} sx={{ mb: 1 }} />
                          <Box display="flex" alignItems="center" sx={{ mb: 0.5 }}>
                            <Skeleton variant="circular" width={16} height={16} sx={{ mr: 1 }} />
                            <Skeleton variant="text" width="50%" height={20} />
                          </Box>
                          <Box display="flex" alignItems="center">
                            <Skeleton variant="circular" width={16} height={16} sx={{ mr: 1 }} />
                            <Skeleton variant="text" width="40%" height={20} />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : appointments.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  No appointments found for this customer.
                </Typography>
              ) : (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {appointments.map((appointment) => (
                    <Grid item xs={12} key={appointment.id}>
                      <Card
                        variant="outlined"
                        sx={{
                          cursor: 'pointer',
                          borderLeft: `10px solid ${getAppointmentTypeColor(appointment.appointmentTypeId)}`,
                          '&:hover': {
                            backgroundColor: 'action.hover'
                          }
                        }}
                        onClick={() => handleAppointmentClick(appointment)}
                      >
                        <CardContent>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                            <Box flex={1}>
                              <Typography variant="h6" gutterBottom>
                                {appointment.title}
                              </Typography>
                              
                              {appointment.description && (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                  {appointment.description}
                                </Typography>
                              )}
                              
                              <Box display="flex" alignItems="center" sx={{ mb: 0.5 }}>
                                <CalendarIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                                <Typography variant="body2">
                                  {new Date(appointment.startTime).toLocaleString('en-GB')} - {new Date(appointment.endTime).toLocaleString('en-GB')}
                                </Typography>
                              </Box>
                              
                              {appointment.address && (
                                <Box display="flex" alignItems="center">
                                  <LocationIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                                  <Typography variant="body2">
                                    {appointment.address}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </CardContent>
          </Card>
        </TabPanel>

        {/* Dynamic Job Tabs */}
        {jobs.map((job, index) => (
          <TabPanel key={job.id} value={tabValue} index={3 + index}>
            <EmbeddedJobDetailPage
              jobId={job.id}
              customerId={job.customerId}
              appointments={appointments}
              currentCustomerId={id || ''}
              appointmentTypes={appointmentTypes}
              business={business}
            />
          </TabPanel>
        ))}

        {/* Edit Customer Dialog */}
        <BaseDialog 
          open={isEditDialogOpen} 
          onClose={() => setIsEditDialogOpen(false)}
          title="Edit Customer"
          fullWidth
          maxWidth="md"
          contentSx={{ pb: 4 }}
          actions={
            <>
              <Button 
                onClick={() => setIsEditDialogOpen(false)}
                variant="outlined"
                color="primary"
              >
                Cancel
              </Button>
              <Button onClick={handleUpdateCustomer} variant="contained">Update</Button>
            </>
          }
        >
          {error && <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.firstName}
                onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.lastName}
                onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.phoneNumber}
                onChange={(e) => setFormData(prev => ({ ...prev, phoneNumber: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <GoogleMapsAutocomplete
                value={formData.address || ''}
                onChange={(address) => setFormData(prev => ({ ...prev, address }))}
                label="Address"
                defaultBounds={{
                  city: business?.defaultCity,
                  country: business?.defaultCountry
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="City"
                value={formData.city}
                onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Country"
                value={formData.country}
                onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Secondary Contact Name"
                value={formData.secondaryContactName}
                onChange={(e) => setFormData(prev => ({ ...prev, secondaryContactName: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Secondary Contact Phone"
                type="tel"
                value={formData.secondaryContactPhone}
                onChange={(e) => setFormData(prev => ({ ...prev, secondaryContactPhone: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Secondary Contact Email"
                type="email"
                value={formData.secondaryContactEmail}
                onChange={(e) => setFormData(prev => ({ ...prev, secondaryContactEmail: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Source</InputLabel>
                <Select
                  value={formData.source}
                  onChange={(e) => setFormData(prev => ({ ...prev, source: e.target.value as CustomerSource }))}
                  label="Source"
                >
                  {Object.values(CustomerSource).filter(v => typeof v === 'number').map((source) => (
                    <MenuItem key={source} value={source}>
                      {customerService.getSourceDisplayName(source as CustomerSource)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Assigned Sales Person</InputLabel>
                <Select
                  value={formData.assignedSalesPersonId || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, assignedSalesPersonId: e.target.value || undefined } as UpdateCustomerDto))}
                  label="Assigned Sales Person"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {businessUsers.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.firstName} {user.lastName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isDifficultPayer || false}
                    onChange={(e) => setFormData(prev => ({ ...prev, isDifficultPayer: e.target.checked }))}
                    color="warning"
                  />
                }
                label="Difficult Payer"
              />
            </Grid>
            {formData.isDifficultPayer && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Difficult Payer Notes"
                  multiline
                  rows={2}
                  value={formData.difficultPayerNotes || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, difficultPayerNotes: e.target.value }))}
                  placeholder="e.g., Late on last 3 invoices, Requires deposit upfront"
                  inputProps={{ maxLength: 2000 }}
                />
              </Grid>
            )}
          </Grid>
        </BaseDialog>

        {/* Create Job Dialog */}
        <BaseDialog
          open={isJobDialogOpen}
          onClose={() => setIsJobDialogOpen(false)}
          title={`Create Job for ${customer?.firstName} ${customer?.lastName}`}
          fullWidth
          sx={{
            '& .MuiDialog-paper': {
              maxWidth: { xs: 'calc(100vw - 10px)', sm: '900px' }
            }
          }}
          contentSx={{ pb: 4 }}
          actions={
            <>
              <Button
                onClick={() => setIsJobDialogOpen(false)}
                variant="outlined"
                color="primary"
              >
                Cancel
              </Button>
              <Button onClick={handleCreateJob} variant="contained">Create Job</Button>
            </>
          }
        >
          {error && <Alert severity="error" sx={{ mb: 2, mt: 1, whiteSpace: 'pre-line' }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2, mt: 1 }}>{success}</Alert>}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Assigned Sales Person</InputLabel>
                <Select
                  value={jobFormData.assignedSalesPersonId || ''}
                  onChange={(e) => setJobFormData(prev => ({ ...prev, assignedSalesPersonId: e.target.value }))}
                  label="Assigned Sales Person"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {businessUsers.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.firstName} {user.lastName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <JobForm
              formData={jobFormData}
              onFormDataChange={setJobFormData}
              defaultBounds={{
                city: business?.defaultCity,
                country: business?.defaultCountry
              }}
              showCustomerSelection={false}
              showValueFields={false}
            />
          </Grid>
        </BaseDialog>

        {/* Duplicate Job Dialog */}
        <DuplicateJobDialog
          open={isDuplicateDialogOpen}
          onClose={() => setIsDuplicateDialogOpen(false)}
          onDuplicate={handleConfirmDuplicateJob}
          duplicateAddress={duplicateAddress}
          onAddressChange={setDuplicateAddress}
          isDuplicating={duplicateJobMutation.isPending}
          defaultBounds={{
            city: business?.defaultCity,
            country: business?.defaultCountry
          }}
        />

        {/* Edit Job Dialog */}
        <BaseDialog
          open={isJobEditDialogOpen}
          onClose={() => setIsJobEditDialogOpen(false)}
          title="Edit Job"
          fullWidth
          maxWidth="md"
          contentSx={{ pb: 4 }}
          actions={
            <>
              <Button
                onClick={() => setIsJobEditDialogOpen(false)}
                variant="outlined"
                color="primary"
              >
                Cancel
              </Button>
              <Button onClick={handleUpdateJob} variant="contained">Update</Button>
            </>
          }
        >
          {error && <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          <Grid container spacing={2}>
            {customer && (
              <Grid item xs={12}>
                <CustomerDetailsCard customer={customer} />
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Assigned Sales Person</InputLabel>
                <Select
                  value={jobEditFormData.assignedSalesPersonId || ''}
                  onChange={(e) => setJobEditFormData(prev => ({ ...prev, assignedSalesPersonId: e.target.value }))}
                  label="Assigned Sales Person"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {businessUsers.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.firstName} {user.lastName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <JobForm
              formData={jobEditFormData}
              onFormDataChange={(data) => setJobEditFormData({ ...jobEditFormData, ...data })}
              defaultBounds={{
                city: business?.defaultCity,
                country: business?.defaultCountry
              }}
              showCustomerSelection={false}
            />
          </Grid>
        </BaseDialog>
      </Box>
    </>
  );
};

export default CustomerDetailPage;
